import { useState, useCallback } from 'react';

// Define the pending action type
export interface PendingAction {
  type: string;
  payload?: any;
}

/**
 * Hook for managing unsaved changes and pending actions
 */
export const useUnsavedChanges = () => {
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  /**
   * Set the unsaved changes flag
   */
  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    setUnsavedChanges(hasChanges);
  }, []);

  /**
   * Handle an action that might be blocked by unsaved changes
   * @param action The action to perform
   * @returns true if the action was performed, false if it was blocked
   */
  const handlePotentiallyBlockedAction = useCallback(
    (action: PendingAction): boolean => {
      if (unsavedChanges) {
        setPendingAction(action);
        setShowUnsavedDialog(true);
        return false;
      } else {
        // No unsaved changes, perform the action immediately
        return true;
      }
    },
    [unsavedChanges]
  );

  /**
   * Handle the user's decision in the unsaved changes dialog
   * @param save Whether to save changes before proceeding
   * @param onSave Function to call to save changes
   * @param onProceed Function to call to proceed with the pending action
   */
  const handleDialogAction = useCallback(
    (save: boolean, onSave: () => Promise<void>, onProceed: (action: PendingAction) => void) => {
      setShowUnsavedDialog(false);

      const proceed = () => {
        if (pendingAction) {
          onProceed(pendingAction);
          setPendingAction(null);
        }
        setUnsavedChanges(false);
      };

      if (save) {
        // Save changes then proceed
        onSave().then(proceed);
      } else {
        // Discard changes and proceed
        proceed();
      }
    },
    [pendingAction]
  );

  /**
   * Cancel the pending action and close the dialog
   */
  const cancelPendingAction = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingAction(null);
  }, []);

  return {
    unsavedChanges,
    setHasUnsavedChanges,
    showUnsavedDialog,
    pendingAction,
    handlePotentiallyBlockedAction,
    handleDialogAction,
    cancelPendingAction,
  };
};

export default useUnsavedChanges;
